import { db, serverTimestamp } from "../../firebaseClient";
import type { ContractRepoDoc } from "../types/contractRepo";
import type { AppUser } from "../context/AuthContext";
import { getDeptCodeByEmail, isSuperUser } from "../config/roles";

export type RepoType = "main" | "legacy";

/**
 * NOTE (2026-01):
 * - 「合約留存管理（資料庫）」使用 legal_contracts 集合，並以 isLegacy 欄位控制「顯示舊合約」。
 * - 舊合約登錄（LegacyContractsPage）非草稿資料也必須寫入同一個 legal_contracts，才能在資料庫頁顯示。
 * - legacyContracts 集合保留作為「草稿/未完成」暫存（若有）。
 */
const COLLECTIONS = {
  main: "contracts", // legacy 狀態變更前的主 repo（保留相容）
  legacyDrafts: "legacyContracts",
  unifiedContracts: "legal_contracts",
};

function isPlainObject(v: any): v is Record<string, any> {
  if (!v || typeof v !== "object") return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/**
 * Firestore does NOT allow "undefined" as a field value.
 * This helper strips undefined recursively from plain objects / arrays.
 */
function stripUndefinedDeep<T>(input: T): T {
  if (input === undefined) return input;
  if (Array.isArray(input)) {
    const arr = (input as any[])
      .map((v) => stripUndefinedDeep(v))
      .filter((v) => v !== undefined);
    return arr as any;
  }
  if (isPlainObject(input)) {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(input as any)) {
      const cleaned = stripUndefinedDeep(v);
      if (cleaned === undefined) continue;
      out[k] = cleaned;
    }
    return out as any;
  }
  // Keep non-plain objects intact (e.g., Firestore FieldValue/Timestamp/Date)
  return input;
}

function normalizeDeptCode(input: string): string {
  const s = String(input || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
  // e.g. "行銷設計部" -> "MK"：這裡只做最小處理，實際轉換以 roles.ts 的 getDeptCodeByEmail 為準
  return s;
}

function isDraftLike(status: any, data: any): boolean {
  if (data?.isDraft === true) return true;
  const s = String(status || "").trim().toLowerCase();
  if (!s) return false;
  return s === "draft" || s === "d" || s.includes("草稿") || s === "pending_draft";
}

function toRepoDoc(raw: any): ContractRepoDoc {
  const number = raw.number || raw.contractNo || raw.id || "";
  const contractNo = raw.contractNo || raw.number || number;
  const effectiveDate = raw.effectiveDate || raw.periodStart || raw.period_start || raw.signDate || raw.signedDate || "";
  const expiryDate = raw.expiryDate || raw.periodEnd || raw.period_end || "";

  // 兼容：demoSeed / 舊資料可能用 notes / periodStart/periodEnd
  const summary = raw.summary || raw.notes || "";

  return {
    ...(raw as any),
    id: raw.id,
    number,
    contractNo,
    effectiveDate,
    expiryDate,
    signedDate: raw.signedDate || raw.signDate || effectiveDate,
    summary,
  } as any;
}

function sortByCreatedAtDesc(list: any[]) {
  return list.sort((a, b) => {
    const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
    const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
    return tb - ta;
  });
}

export async function getRepoItems(type: RepoType, user: AppUser): Promise<ContractRepoDoc[]> {
  if (!user?.email) return [];

  const superUser = isSuperUser(user.email);
  const deptCode = normalizeDeptCode(getDeptCodeByEmail(user.email) || "");

  // --------
  // Legacy：
  // - 主要來源：legal_contracts（isLegacy=true） -> 讓「合約留存管理」勾選「顯示舊合約」即可看到
  // - 相容來源：legacyContracts（草稿/舊資料殘留）
  // --------
  if (type === "legacy") {
    const merged: any[] = [];

    // 1) unifiedContracts: legal_contracts
    try {
      const snap = await db.collection(COLLECTIONS.unifiedContracts).orderBy("createdAt", "desc").get();
      snap.forEach((doc) => {
        const d = doc.data() as any;
        if (!d) return;
        if (!d.isLegacy) return;
        merged.push({ id: doc.id, ...d, __store: "legal_contracts" });
      });
    } catch (e) {
      console.warn("[contractRepoService] legacy fetch legal_contracts failed", e);
    }

    // 2) legacyDrafts: legacyContracts（草稿/舊資料保留）
    try {
      const snap = await db.collection(COLLECTIONS.legacyDrafts).orderBy("createdAt", "desc").get();
      snap.forEach((doc) => {
        const d = doc.data() as any;
        if (!d) return;
        merged.push({ id: doc.id, ...d, __store: "legacyContracts" });
      });
    } catch (e) {
      console.warn("[contractRepoService] legacy fetch legacyContracts failed", e);
    }

    // dept isolation (避免需要額外 Firestore index，改由前端過濾)
    const deptFiltered = superUser
      ? merged
      : merged.filter((it) => normalizeDeptCode(it.dept || "") === deptCode);

    // 去重：以 contractNo/number 為主鍵，同一筆資料優先保留 legal_contracts 版本
    const byKey = new Map<string, any>();
    for (const it of deptFiltered) {
      const key = String(it.contractNo || it.number || it.id || "").trim();
      if (!key) continue;
      const prev = byKey.get(key);
      if (!prev) {
        byKey.set(key, it);
        continue;
      }
      // prefer unified (legal_contracts)
      if (prev.__store !== "legal_contracts" && it.__store === "legal_contracts") {
        byKey.set(key, it);
      }
    }

    const normalized = Array.from(byKey.values()).map(toRepoDoc);
    return sortByCreatedAtDesc(normalized) as any;
  }

  // --------
  // Main：維持舊行為（contracts 集合）
  // --------
  const colName = COLLECTIONS.main;
  const q = superUser
    ? db.collection(colName).orderBy("createdAt", "desc")
    : db.collection(colName).where("dept", "==", deptCode).orderBy("createdAt", "desc");

  const snap = await q.get();
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as any;
}

export async function saveRepoItem(type: RepoType, data: Partial<ContractRepoDoc>) {
  const normalizedDept = normalizeDeptCode(String((data as any).dept || ""));

  if (type === "legacy") {
    const incomingId = String((data as any).id || "").trim();
    const contractNo = String((data as any).contractNo || (data as any).number || "").trim();
    const draft = isDraftLike((data as any).status, data);

    // --- Draft stays in legacyContracts ---
    if (draft) {
      const payload: any = stripUndefinedDeep({
        ...(data as any),
        dept: normalizedDept,
        status: "draft",
        isLegacy: true,
        updatedAt: serverTimestamp(),
      });
      delete payload.id;

      if (incomingId) {
        await db.collection(COLLECTIONS.legacyDrafts).doc(incomingId).set(payload, { merge: true });
      } else {
        await db.collection(COLLECTIONS.legacyDrafts).add(stripUndefinedDeep({ ...payload, createdAt: serverTimestamp() }));
      }
      return;
    }

    // --- Non-draft legacy contracts must be stored in legal_contracts ---
    const docId = contractNo || incomingId;
    if (!docId) throw new Error("缺少 contractNo/number，無法歸檔到合約資料庫");

    const payload: any = stripUndefinedDeep({
      ...(data as any),
      dept: normalizedDept,

      // Ensure both keys exist
      contractNo: contractNo || (data as any).contractNo || (data as any).number || docId,
      number: (data as any).number || (data as any).contractNo || contractNo || docId,

      // Compatibility fields for other modules/services
      signDate:
        (data as any).signDate ||
        (data as any).signedDate ||
        (data as any).effectiveDate ||
        (data as any).periodStart ||
        "",
      signedDate:
        (data as any).signedDate ||
        (data as any).signDate ||
        (data as any).effectiveDate ||
        (data as any).periodStart ||
        "",
      periodStart: (data as any).periodStart || (data as any).effectiveDate || "",
      periodEnd: (data as any).periodEnd || (data as any).expiryDate || "",
      effectiveDate: (data as any).effectiveDate || (data as any).periodStart || "",
      expiryDate: (data as any).expiryDate || (data as any).periodEnd || "",
      notes: (data as any).notes || (data as any).summary || "",
      summary: (data as any).summary || (data as any).notes || "",

      // key flag: enable "顯示舊合約" in 合約留存管理
      isLegacy: true,

      updatedAt: serverTimestamp(),
    });
    delete payload.id;

    const ref = db.collection(COLLECTIONS.unifiedContracts).doc(docId);
    const snap = await ref.get();
    const toWrite = stripUndefinedDeep({
      ...payload,
      ...(snap.exists ? {} : { createdAt: serverTimestamp() }),
    });
    await ref.set(toWrite as any, { merge: true });

    // Migration cleanup: Non-draft legacy should NOT stay in legacyContracts.
    // Always attempt to remove the old doc (safe even if it doesn't exist).
    if (incomingId) {
      try {
        await db.collection(COLLECTIONS.legacyDrafts).doc(incomingId).delete();
      } catch {
        // ignore
      }
    }

    return;
  }

  // --------
  // Main：維持舊行為（contracts 集合）
  // --------
  const colName = COLLECTIONS.main;
  const payload: any = stripUndefinedDeep({
    ...(data as any),
    dept: normalizedDept,
    updatedAt: serverTimestamp(),
  });
  delete payload.id;

  if ((data as any).id) {
    await db.collection(colName).doc(String((data as any).id)).set(payload, { merge: true });
  } else {
    await db.collection(colName).add(stripUndefinedDeep({ ...payload, createdAt: serverTimestamp() }));
  }
}

export async function deleteRepoItem(type: RepoType, id: string) {
  if (!id) return;

  if (type === "legacy") {
    // Try delete from unified store first, then draft store as fallback.
    try {
      await db.collection(COLLECTIONS.unifiedContracts).doc(id).delete();
    } catch {
      // ignore
    }
    try {
      await db.collection(COLLECTIONS.legacyDrafts).doc(id).delete();
    } catch {
      // ignore
    }
    return;
  }

  // main
  await db.collection(COLLECTIONS.main).doc(id).delete();
}
